/**
 * MarginalContributionPanel — the "Contributo ETF" level of the Diversificazione
 * area (Phase R2, contract R1.0).
 *
 * One question per ETF: "quanto cambierebbe il portafoglio se questo ETF non ci
 * fosse?". Everything shown comes from `GET /diversification/redundancy`: the
 * Leave-One-Out scenario, the overlap with the rest, the marginal exposure
 * change, the Diversification Score before/after and the duplication. No metric
 * is recomputed here and no label classifies an ETF: R2 describes, it does not
 * rank, suggest or recommend.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, ArrowRight, Loader2, ArrowUpDown } from "lucide-react";
import { SectionCard } from "@/components/system/SectionCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EtfContributionDialog } from "./EtfContributionDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const num = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : Number(value).toFixed(digits);
const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${num(value, digits)}%`;
const pp = (value, digits = 2) =>
  value === null || value === undefined ? "n/d" : `${num(value, digits)} pp`;
const signedPp = (value, digits = 2) => {
  if (value === null || value === undefined) return "n/d";
  const abs = num(Math.abs(value), digits);
  if (Number(value) === 0) return `0.00 pp`;
  return `${Number(value) > 0 ? "+" : "−"}${abs} pp`;
};
const score = (value) =>
  value === null || value === undefined ? "n/d" : num(value, 0);

const RELIABILITY = {
  high: "affidabilità alta",
  usable: "affidabilità media",
  insufficient: "dati insufficienti",
  no_data: "nessun dato su questa dimensione",
};

/** Overlap of the ETF with the rest: never a confident number when unsupported. */
const overlapText = (etf) => {
  const overlap = etf.overlap_with_rest;
  if (!overlap) return "n/d";
  if (!overlap.supported) {
    return `n/d (fra ${pct(overlap.bracket_min_pct, 1)} e ${pct(overlap.bracket_max_pct, 1)})`;
  }
  return pct(overlap.classified_overlap_pct, 1);
};

const mecText = (etf) => {
  const mec = etf.marginal_exposure_change;
  if (!mec) return "n/d";
  if (!mec.supported || mec.observed === null || mec.observed === undefined) {
    return `almeno ${pp(mec.minimum)} · fino a ${pp(mec.maximum)}`;
  }
  return pp(mec.observed);
};

/**
 * Deterministic sentence, built only from values the backend already published.
 * Describes the facts; it never suggests an action and never classifies the ETF.
 */
export const contributionSummary = (etf, dimensionLabel) => {
  if (etf.status === "zero_weight") return etf.reason;
  if (etf.status !== "ok") return etf.reason;
  const dim = (dimensionLabel || "").toLowerCase();
  const mec = etf.marginal_exposure_change || {};
  const first = mec.supported && mec.observed !== null && mec.observed !== undefined
    ? `Rimuovendolo, l'esposizione per ${dim} cambierebbe di ${pp(mec.observed)}`
    : `La copertura dei dati non consente di stimare con precisione il cambiamento per ${dim}: il valore garantito è compreso tra ${pp(mec.minimum)} e ${pp(mec.maximum)}`;

  const cmp = etf.score || {};
  let second;
  if (!cmp.comparable) {
    second = "il Diversification Score dei due scenari non è confrontabile";
  } else if (score(cmp.score_before) === score(cmp.score_after)) {
    second = `il Diversification Score resterebbe sostanzialmente invariato (${score(cmp.score_before)})`;
  } else {
    second = `il Diversification Score passerebbe da ${score(cmp.score_before)} a ${score(cmp.score_after)}`;
  }
  return `${first}; ${second}.`;
};

const COLUMNS = [
  { id: "weight", label: "Peso", value: (etf) => etf.weight_pct ?? 0 },
  {
    id: "overlap",
    label: "Overlap con il resto",
    value: (etf) => (etf.overlap_with_rest?.supported
      ? etf.overlap_with_rest.classified_overlap_pct ?? -1 : -1),
  },
  {
    id: "mec",
    label: "Cambio delle esposizioni",
    value: (etf) => (etf.marginal_exposure_change?.supported
      ? etf.marginal_exposure_change.observed ?? -1 : -1),
  },
  {
    id: "mdc",
    label: "Contributo allo score",
    value: (etf) => (etf.marginal_diversification_contribution === null
    || etf.marginal_diversification_contribution === undefined
      ? Number.NEGATIVE_INFINITY : etf.marginal_diversification_contribution),
  },
  {
    id: "duplication",
    label: "Duplicazione",
    value: (etf) => (etf.duplication_relief_pct === null
    || etf.duplication_relief_pct === undefined
      ? Number.NEGATIVE_INFINITY : etf.duplication_relief_pct),
  },
];

export function MarginalContributionPanel({
  sessionId, dimension, dimensionLabel, cache, allocationSignature = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(null);           // null = portfolio order
  const [detail, setDetail] = useState(null);
  // The button that opened the detail, so closing it gives the focus back.
  const detailTrigger = useRef(null);

  const openDetail = (etf) => {
    detailTrigger.current = document.activeElement;
    setDetail(etf);
  };

  const closeDetail = () => {
    setDetail(null);
    const trigger = detailTrigger.current;
    detailTrigger.current = null;
    if (trigger && document.contains(trigger)) {
      requestAnimationFrame(() => trigger.focus());
    }
  };

  // The weights are part of the identity of the reading: after a reweight in the
  // Allocazione tab the key changes and the batch is asked again.
  const cacheKey = `${sessionId}|${dimension}|${allocationSignature}`;

  useEffect(() => {
    if (!sessionId) return undefined;
    const stored = cache?.get(cacheKey);
    if (stored && typeof stored.then !== "function") {
      setData(stored);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    // A reading already in flight is awaited instead of asked twice: the batch
    // costs seconds, and the level can be reopened while it is running.
    const request = stored || axios
      .get(`${API}/diversification/redundancy/${sessionId}`, { params: { dimension } })
      .then(({ data: payload }) => {
        cache?.set(cacheKey, payload);
        return payload;
      })
      .catch((err) => {
        cache?.delete(cacheKey);
        throw err;
      });
    cache?.set(cacheKey, request);
    request
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch(() => {
        if (!cancelled) {
          setError("Non è stato possibile calcolare il contributo marginale degli ETF.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, dimension, cacheKey, cache]);

  const rows = useMemo(() => {
    const list = data?.etfs || [];
    if (!sort) return list;
    const column = COLUMNS.find((c) => c.id === sort.id);
    if (!column) return list;
    const copy = [...list];
    copy.sort((a, b) => {
      const delta = column.value(a) - column.value(b);
      return sort.direction === "asc" ? delta : -delta;
    });
    return copy;
  }, [data, sort]);

  const toggleSort = (id) => {
    setSort((current) => {
      if (!current || current.id !== id) return { id, direction: "desc" };
      if (current.direction === "desc") return { id, direction: "asc" };
      return null;
    });
  };

  const label = dimensionLabel || data?.dimension_label || "";

  return (
    <SectionCard
      testId="contribution-panel"
      title="Contributo ETF: cosa cambierebbe senza ciascun ETF"
      description="Per ogni ETF il portafoglio viene ricostruito senza di lui, redistribuendo il suo peso in proporzione fra gli altri. Si leggono quattro cose distinte, che non vanno sommate né compresse in un unico voto: quanto l'ETF assomiglia al resto, quanto cambierebbe la composizione, quanto cambierebbe il Diversification Score e come reagirebbe la duplicazione. È una lettura strutturale read-only, non una raccomandazione di acquisto o di vendita."
      density="compact"
    >
      <div className="space-y-4">
        {loading && (
          <div
            className="flex items-center gap-2 text-sm text-content-muted"
            role="status"
            data-testid="contribution-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Calcolo di uno scenario per ogni ETF: con molti ETF può richiedere qualche
            secondo. Il resto dell&apos;analisi resta utilizzabile.
          </div>
        )}

        {error && !loading && (
          <p className="text-sm text-danger" data-testid="contribution-error">{error}</p>
        )}

        {data && data.status !== "ok" && (
          <p className="text-sm text-content-secondary" data-testid="contribution-not-applicable">
            {data.reason || "Il contributo marginale richiede almeno due ETF con peso positivo: non esiste un portafoglio residuo con cui confrontare questo ETF."}
          </p>
        )}

        {data && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" data-testid="contribution-dimension">
              {label}
            </Badge>
            <Badge variant="secondary" data-testid="contribution-score-before">
              Score attuale {score(data.score_before?.score)}
            </Badge>
            {data.audit?.served_from_cache && (
              <Badge variant="outline" data-testid="contribution-cached">
                lettura già calcolata
              </Badge>
            )}
            {(data.flags || []).map((flag) => (
              <Badge key={flag} variant="warning" data-testid={`contribution-flag-${flag}`}>
                {flag === "restored_snapshot"
                  ? "analisi ripristinata"
                  : flag === "unclassified_mass_present"
                    ? "massa non classificata presente"
                    : flag === "score_not_computable"
                      ? "score non calcolabile"
                      : flag}
              </Badge>
            ))}
          </div>
        )}

        {data && (data.etfs || []).length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[880px]" data-testid="contribution-table">
                <caption className="sr-only">
                  Contributo marginale di ogni ETF del portafoglio su {label}
                </caption>
                <thead className="text-label text-content-muted">
                  <tr className="border-b border-border">
                    <th scope="col" className="px-3 py-2 text-left font-medium">ETF</th>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        scope="col"
                        className="px-3 py-2 text-right font-medium"
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(column.id)}
                          className="inline-flex min-h-[40px] items-center gap-1 text-label font-medium text-content-muted hover:text-content"
                          data-testid={`contribution-sort-${column.id}`}
                          aria-label={`Ordina per ${column.label}`}
                        >
                          {column.label}
                          <ArrowUpDown className="w-3 h-3" aria-hidden="true" />
                        </button>
                      </th>
                    ))}
                    <th scope="col" className="px-3 py-2 text-right font-medium">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((etf) => (
                    <tr
                      key={etf.key}
                      className="border-b border-border/60 last:border-0 align-top"
                      data-testid={`contribution-row-${etf.key}`}
                    >
                      <td className="px-3 py-3">
                        <p className="text-content font-medium">{etf.label}</p>
                        <p className="text-label text-content-muted">
                          {[etf.ticker, etf.isin].filter(Boolean).join(" · ") || "—"}
                        </p>
                        <p
                          className="text-label text-content-secondary mt-1 max-w-[34rem]"
                          data-testid={`contribution-summary-${etf.key}`}
                        >
                          {contributionSummary(etf, label)}
                        </p>
                      </td>
                      <td
                        className="px-3 py-3 text-right tabular text-content"
                        data-testid={`contribution-weight-${etf.key}`}
                      >
                        {pct(etf.weight_pct, 1)}
                      </td>
                      <td
                        className="px-3 py-3 text-right tabular text-content-secondary"
                        data-testid={`contribution-overlap-${etf.key}`}
                      >
                        {etf.status === "ok" ? overlapText(etf) : "—"}
                        {etf.status === "ok" && etf.overlap_with_rest && (
                          <span className="block text-label text-content-muted">
                            {RELIABILITY[etf.overlap_with_rest.reliability]
                              || etf.overlap_with_rest.reliability}
                          </span>
                        )}
                      </td>
                      <td
                        className="px-3 py-3 text-right tabular text-content"
                        data-testid={`contribution-mec-${etf.key}`}
                      >
                        {etf.status === "ok" ? mecText(etf) : "—"}
                      </td>
                      <td
                        className="px-3 py-3 text-right tabular text-content"
                        data-testid={`contribution-mdc-${etf.key}`}
                      >
                        {etf.status !== "ok" ? "—" : (
                          <>
                            <span className="block text-content-secondary">
                              {score(etf.score?.score_before)}
                              <ArrowRight className="inline w-3 h-3 mx-1 text-content-muted" aria-hidden="true" />
                              <span className="sr-only"> senza questo ETF </span>
                              {score(etf.score?.score_after)}
                            </span>
                            <span className="block">
                              {etf.marginal_diversification_contribution === null
                              || etf.marginal_diversification_contribution === undefined
                                ? "non confrontabile"
                                : `${Number(etf.marginal_diversification_contribution) > 0 ? "+" : Number(etf.marginal_diversification_contribution) < 0 ? "−" : ""}${num(Math.abs(etf.marginal_diversification_contribution), 1)} punti`}
                            </span>
                          </>
                        )}
                      </td>
                      <td
                        className="px-3 py-3 text-right tabular text-content-secondary"
                        data-testid={`contribution-duplication-${etf.key}`}
                      >
                        {etf.status === "ok" ? signedPp(etf.duplication_relief_pct) : "—"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button
                          variant="ghost"
                          className="h-10 px-3"
                          disabled={etf.status !== "ok"}
                          onClick={() => openDetail(etf)}
                          data-testid={`contribution-detail-${etf.key}`}
                        >
                          Dettaglio
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-label text-content-muted" data-testid="contribution-order-note">
              L&apos;ordine iniziale è quello del tuo portafoglio: puoi ordinare le colonne per
              esplorare i dati, ma nessun ordinamento è una classifica di ETF migliori o
              peggiori. Un valore positivo del contributo allo score significa che senza
              quell&apos;ETF lo score scenderebbe; un valore negativo che salirebbe.
            </p>
            <p className="text-label text-content-muted" data-testid="contribution-reading-note">
              Un overlap vicino al 100% con un cambio delle esposizioni vicino a 0 pp
              descrive un ETF la cui struttura economica è già presente nel resto del
              portafoglio: è un fatto misurato, non un giudizio. La duplicazione può
              muoversi in direzione diversa dal contributo allo score e i contributi
              Leave-One-Out non sono additivi: la loro somma non ricostruisce né la
              duplicazione né il punteggio del portafoglio.
            </p>
          </>
        )}

        {data && (
          <div className="space-y-2">
            <p className="text-label text-content-muted" data-testid="contribution-provenance">
              {data.provenance?.note}
            </p>
            {(data.warnings || []).map((warning) => (
              <p
                key={warning}
                className="flex items-start gap-2 text-label text-content-muted"
                data-testid="contribution-warning"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{warning}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      <EtfContributionDialog
        open={Boolean(detail)}
        onOpenChange={(next) => { if (!next) closeDetail(); }}
        etf={detail}
        dimensionLabel={label}
        analysis={data}
      />
    </SectionCard>
  );
}

export default MarginalContributionPanel;
